import { apiClient } from "@lib/axios";
import type { AxiosRequestConfig } from "axios";

interface ExportFiles {
  pdfLink?: string;
  pptxLink?: string;
  imagesLink?: string;
  gifLink?: string;
  screenshotLink?: string;
}

type ExportJob =
  | { status: "pending" }
  | { status: "failed"; error?: string }
  | { status: "done"; files?: ExportFiles };

const POLL_INTERVAL_MS = 2500;
const REQUEST_TIMEOUT_MS = 30 * 1000;
// The backend fails pending jobs at 10 minutes; keep the client deadline a bit
// longer so that timeout surfaces as a backend "failed" result, not a client abort.
const DEFAULT_DEADLINE_MS = 11 * 60 * 1000;

const wait = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const cleanup = () => {
      clearTimeout(timer);
      signal?.removeEventListener("abort", onAbort);
    };
    const onAbort = () => {
      cleanup();
      reject(new DOMException("Aborted", "AbortError"));
    };
    const timer = setTimeout(() => {
      cleanup();
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort);
  });

export const headlessBrowserApi = {
  // Starts an async export on the backend (which relays to the IAM-protected
  // headless service) and polls until the one-shot result is ready. The
  // resolved shape matches the old direct headless-service call. A passed
  // `config.timeout` bounds the whole start+poll flow, not a single request.
  generatePdf: async (
    payload: unknown,
    config?: AxiosRequestConfig,
  ): Promise<ExportFiles> => {
    const { timeout, ...requestConfig } = config ?? {};
    const deadline = Date.now() + (timeout || DEFAULT_DEADLINE_MS);
    const signal = requestConfig.signal as AbortSignal | undefined;

    const { jobId } = await apiClient.post<{ jobId: string }>(
      "/exports",
      payload,
      { timeout: REQUEST_TIMEOUT_MS, ...requestConfig },
    );

    while (Date.now() < deadline) {
      await wait(POLL_INTERVAL_MS, signal);
      const job = await apiClient.get<ExportJob>(`/exports/${jobId}`, {
        timeout: REQUEST_TIMEOUT_MS,
        ...requestConfig,
      });
      if (job.status === "done") return job.files ?? {};
      if (job.status === "failed") {
        throw new Error(job.error || "Export failed");
      }
    }

    throw new Error("Export timed out");
  },
};
